import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw, FiX, FiChevronDown } from "react-icons/fi";
import { getPlatformProducts, createPlatformProduct, updatePlatformProduct, deletePlatformProduct } from "../../services/platformProductService";
import { getCommonProducts } from "../../services/commonProductService";

const PLATFORM_OPTIONS = [
  { value: "amazon", label: "Amazon" },
  { value: "flipkart", label: "Flipkart" },
  { value: "shopify", label: "Shopify" },
  { value: "tata 1mg", label: "Tata 1mg" },
  { value: "palsons derma pvt ltd", label: "Palsons Derma Pvt Ltd" },
];

const PlatformProductManagement = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortField, setSortField] = useState("id");
  const [sortValue, setSortValue] = useState("DESC");
  const [postingAccount, setPostingAccount] = useState("");
  const [productId, setProductId] = useState("");
  const [visibility, setVisibility] = useState("");
  const [disabled, setDisabled] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [showUnitsSold, setShowUnitsSold] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const productDropdownRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    productId: "",
    postingAccount: "",
    productName: "",
    slug: "",
    sku: "",
    channelSku: "",
    channelProductId: "",
    sellingPrice: "",
    transferPrice: "",
    mrp: "",
    msp: "",
    currency: "",
    visibility: true,
    disabled: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  // Fetch common products for dropdown
  const { data: commonProductsData } = useQuery(
    ["commonProducts"],
    () => getCommonProducts({ page: 1, limit: 1000 }),
    { refetchOnWindowFocus: false }
  );

  const commonProducts = commonProductsData?.data?.data || [];

  // Filter products based on search term
  const filteredCommonProducts = React.useMemo(() => {
    if (!productSearchTerm.trim()) {
      return commonProducts;
    }
    const searchLower = productSearchTerm.toLowerCase();
    return commonProducts.filter(product =>
      product.name?.toLowerCase().includes(searchLower) ||
      product.baseSku?.toLowerCase().includes(searchLower)
    );
  }, [commonProducts, productSearchTerm]);

  // Get selected product name for display
  const selectedProductName = React.useMemo(() => {
    if (!productId) return "All Products";
    const product = commonProducts.find(p => p.id === parseInt(productId));
    return product?.name || "All Products";
  }, [productId, commonProducts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const { data: platformProductsData, isLoading, refetch } = useQuery(
    ["platformProducts", page, limit, sortField, sortValue, postingAccount, productId, visibility, disabled, searchKey, showUnitsSold],
    () => getPlatformProducts({
      page,
      limit,
      sortField,
      sortValue,
      postingAccount,
      productId,
      visibility,
      disabled,
      searchKey,
      showUnitsSold
    }),
    {
      onError: (error) => {
        toast.error(error.message || "Failed to load platform products");
      },
      refetchOnWindowFocus: false,
    }
  );

  const createProductMutation = useMutation(
    (data) => createPlatformProduct(data),
    {
      onSuccess: () => {
        toast.success("Platform product created successfully");
        queryClient.invalidateQueries("platformProducts");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to create platform product");
      },
    }
  );

  const updateProductMutation = useMutation(
    (data) => updatePlatformProduct(data),
    {
      onSuccess: () => {
        toast.success("Platform product updated successfully");
        queryClient.invalidateQueries("platformProducts");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update platform product");
      },
    }
  );

  const deleteProductMutation = useMutation(
    (platformProductId) => deletePlatformProduct(platformProductId),
    {
      onSuccess: () => {
        toast.success("Platform product deleted successfully");
        queryClient.invalidateQueries("platformProducts");
      },
      onError: (error) => {
        toast.error(error.message || "Failed to delete platform product");
      },
    }
  );

  // Extract data from server response
  const products = React.useMemo(() => {
    if (!platformProductsData || !platformProductsData.data) return [];
    return Array.isArray(platformProductsData.data.data) ? platformProductsData.data.data : [];
  }, [platformProductsData]);

  const totalCount = platformProductsData?.data?.count || 0;
  const hasNext = platformProductsData?.data?.hasNext || false;
  const totalPages = Math.ceil(totalCount / limit);

  const resetForm = () => {
    setFormData({
      productId: "",
      postingAccount: "",
      productName: "",
      slug: "",
      sku: "",
      channelSku: "",
      channelProductId: "",
      sellingPrice: "",
      transferPrice: "",
      mrp: "",
      msp: "",
      currency: "",
      visibility: true,
      disabled: false
    });
    setSelectedProduct(null);
  };

  const handleCreate = () => {
    setModalType("create");
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setModalType("edit");
    setSelectedProduct(product);
    setFormData({
      productId: product.productId?.toString() || "",
      postingAccount: product.postingAccount || "",
      productName: product.productName || "",
      slug: product.slug || "",
      sku: product.sku || "",
      channelSku: product.channelSku || "",
      channelProductId: product.channelProductId || "",
      sellingPrice: product.sellingPrice?.toString() || "",
      transferPrice: product.transferPrice?.toString() || "",
      mrp: product.mrp?.toString() || "",
      msp: product.msp?.toString() || "",
      currency: product.currency || "",
      visibility: product.visibility !== undefined ? product.visibility : true,
      disabled: product.disabled !== undefined ? product.disabled : false
    });
    setShowModal(true);
  };

  const handleDelete = (product) => {
    if (window.confirm(`Are you sure you want to delete platform product "${product.productName}"?`)) {
      deleteProductMutation.mutate(product.id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        productId: formData.productId ? parseInt(formData.productId) : null,
        sellingPrice: formData.sellingPrice ? parseFloat(formData.sellingPrice) : null,
        transferPrice: formData.transferPrice ? parseFloat(formData.transferPrice) : null,
        mrp: formData.mrp ? parseFloat(formData.mrp) : null,
        msp: formData.msp ? parseFloat(formData.msp) : null,
      };
      if (modalType === "create") {
        await createProductMutation.mutateAsync(submitData);
      } else {
        await updateProductMutation.mutateAsync({
          platformProductId: selectedProduct.id,
          ...submitData
        });
      }
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setPage(1);
  }, [searchKey, postingAccount, productId, visibility, disabled, showUnitsSold, sortField, sortValue]);

  const getCommonProductName = (prodId) => {
    const product = commonProducts.find(p => p.id === prodId);
    return product?.name || "Unlinked";
  };

  const getPlatformLabel = (platform) => {
    const platformOption = PLATFORM_OPTIONS.find(p => p.value === platform);
    return platformOption?.label || platform;
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Platform Product Management</h1>
        <p className="text-gray-600">Manage platform-specific product listings</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search platform products..."
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>

            <select
              value={postingAccount}
              onChange={(e) => setPostingAccount(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Platforms</option>
              {PLATFORM_OPTIONS.map(platform => (
                <option key={platform.value} value={platform.value}>{platform.label}</option>
              ))}
            </select>

            <div className="relative" ref={productDropdownRef}>
              <button
                type="button"
                onClick={() => setShowProductDropdown(!showProductDropdown)}
                className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between group"
                title={selectedProductName !== "All Products" ? selectedProductName : ""}
              >
                <span className="truncate" title={selectedProductName !== "All Products" ? selectedProductName : ""}>
                  {selectedProductName}
                </span>
                <FiChevronDown className={`ml-2 flex-shrink-0 transition-transform ${showProductDropdown ? 'transform rotate-180' : ''}`} size={16} />
              </button>
              
              {showProductDropdown && (
                <div className="absolute z-50 mt-1 w-full sm:w-64 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  <div className="p-2 border-b border-gray-200">
                    <div className="relative">
                      <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="text"
                        placeholder="Search products..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        autoFocus
                      />
                      {productSearchTerm && (
                        <button
                          onClick={() => setProductSearchTerm("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <FiX size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-64">
                    <button
                      type="button"
                      onClick={() => {
                        setProductId("");
                        setShowProductDropdown(false);
                        setProductSearchTerm("");
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                        !productId ? "bg-blue-50 text-blue-600" : "text-gray-700"
                      }`}
                    >
                      All Products
                    </button>
                    {filteredCommonProducts.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500 text-center">
                        No products found
                      </div>
                    ) : (
                      filteredCommonProducts.map(product => (
                        <button
                          key={product.id}
                          type="button"
                          onClick={() => {
                            setProductId(product.id.toString());
                            setShowProductDropdown(false);
                            setProductSearchTerm("");
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 truncate ${
                            productId === product.id.toString() ? "bg-blue-50 text-blue-600" : "text-gray-700"
                          }`}
                          title={product.name}
                        >
                          <div className="font-medium">{product.name}</div>
                          {product.baseSku && (
                            <div className="text-xs text-gray-500">SKU: {product.baseSku}</div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Visibility</option>
              <option value="true">Visible</option>
              <option value="false">Hidden</option>
            </select>

            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={showUnitsSold}
                onChange={(e) => setShowUnitsSold(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show units sold</span>
            </label>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FiRefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
            >
              <FiPlus size={16} className="mr-2" />
              Add
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Product</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Channel SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                {showUnitsSold && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Units sold</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={showUnitsSold ? 8 : 7} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={showUnitsSold ? 8 : 7} className="px-6 py-12 text-center text-gray-500">No platform products found</td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{product.productName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getPlatformLabel(product.postingAccount)}
                    </td>
                    <td className="px-6 py-4  text-sm text-gray-500">
                      {product.productId ? (
                        <Link
                          to={{
                            pathname: `/products/common/${product.productId}`,
                            state: { fromPlatformProduct: true, tab: "platformProducts" },
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {getCommonProductName(product.productId)}
                        </Link>
                      ) : (
                        <span className="text-gray-400">Unlinked</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.channelSku || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {product.sellingPrice ? `₹${product.sellingPrice.toFixed(2)}` : "-"}
                    </td>
                    {showUnitsSold && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {product.unitsSold != null ? product.unitsSold : "-"}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.visibility ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {product.visibility ? "Visible" : "Hidden"}
                        </span>
                        {product.disabled && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Disabled
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button onClick={() => handleEdit(product)} className="text-indigo-600 hover:text-indigo-900 p-1 rounded">
                          <FiEdit2 size={16} />
                        </button>
                        <button onClick={() => handleDelete(product)} className="text-red-600 hover:text-red-900 p-1 rounded">
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {totalCount > 0 ? (page - 1) * limit + 1 : 0}
                  </span> to{" "}
                  <span className="font-medium">
                    {Math.min(page * limit, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 10) {
                      pageNum = i + 1;
                    } else if (page <= 5) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 4) {
                      pageNum = totalPages - 9 + i;
                    } else {
                      pageNum = page - 5 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === page
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === "create" ? "Create Platform Product" : "Edit Platform Product"}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
                    <select
                      value={formData.postingAccount}
                      onChange={(e) => setFormData({ ...formData, postingAccount: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select Platform</option>
                      {PLATFORM_OPTIONS.map(platform => (
                        <option key={platform.value} value={platform.value}>{platform.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link to Common Product</label>
                    <select
                      value={formData.productId}
                      onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Product (Optional)</option>
                      {commonProducts.map(product => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name (Platform) *</label>
                  <input
                    type="text"
                    value={formData.productName}
                    onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <input
                      type="text"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel SKU</label>
                    <input
                      type="text"
                      value={formData.channelSku}
                      onChange={(e) => setFormData({ ...formData, channelSku: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Channel Product ID</label>
                    <input
                      type="text"
                      value={formData.channelProductId}
                      onChange={(e) => setFormData({ ...formData, channelProductId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selling Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.sellingPrice}
                      onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.transferPrice}
                      onChange={(e) => setFormData({ ...formData, transferPrice: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MRP</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.mrp}
                      onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">MSP</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.msp}
                      onChange={(e) => setFormData({ ...formData, msp: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    <input
                      type="text"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="INR"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                    <select
                      value={formData.visibility}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value === "true" })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={true}>Visible</option>
                      <option value={false}>Hidden</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Disabled</label>
                  <select
                    value={formData.disabled}
                    onChange={(e) => setFormData({ ...formData, disabled: e.target.value === "true" })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={false}>Active</option>
                    <option value={true}>Disabled</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                  >
                    {isSubmitting ? "Saving..." : modalType === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformProductManagement;

